import React from 'react'
import type { Block } from '../../editor/types'
import {
  ArrowRight, ExternalLink, Download, Send, Play, Plus, Check,
  Star, Heart, Zap, Sparkles, Rocket, Gift, ShoppingCart,
  Mail, Phone, Calendar, Share2, Link, Eye,
} from 'lucide-react'

interface ButtonBlockProps {
  block: Block
  variableValues: Record<string, number | string | boolean>
  onSetVariableValue: (name: string, value: number | string | boolean) => void
}

const AVAILABLE_ICONS = [
  'ArrowRight', 'ExternalLink', 'Download', 'Send', 'Play', 'Plus', 'Check',
  'Star', 'Heart', 'Zap', 'Sparkles', 'Rocket', 'Gift', 'ShoppingCart',
  'Mail', 'Phone', 'Calendar', 'Share2', 'Link', 'Eye',
] as const

export { AVAILABLE_ICONS }

const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  ArrowRight, ExternalLink, Download, Send, Play, Plus, Check,
  Star, Heart, Zap, Sparkles, Rocket, Gift, ShoppingCart,
  Mail, Phone, Calendar, Share2, Link, Eye,
}

function getIcon(iconName?: string) {
  if (!iconName) return null
  const Icon = ICON_MAP[iconName]
  return Icon ? <Icon className="h-4 w-4" /> : null
}

export default function ButtonBlock({ block, variableValues, onSetVariableValue }: ButtonBlockProps) {
  const config = (block.config || {}) as Record<string, unknown>
  const variant = (config.variant as string) || 'primary'
  const href = (config.href as string) || undefined
  const newTab = (config.newTab as boolean) || false
  const iconName = (config.icon as string) || undefined
  const accent = block.accent || 'var(--accent, #5048e5)'

  const handleClick = () => {
    // Toggle own variable (true <-> false)
    if (block.variable) {
      const current = variableValues[block.variable]
      onSetVariableValue(block.variable, !current)
    }
    // Also support "set other variable" action
    if (block.config) {
      const cfg = block.config as Record<string, unknown>
      const targetVar = cfg.targetVariable as string | undefined
      const targetVal = cfg.targetValue as number | string | boolean | undefined
      if (targetVar && targetVal !== undefined) {
        onSetVariableValue(targetVar, targetVal)
      }
    }
  }

  const icon = getIcon(iconName)
  const label = block.label || 'Button'
  const align = config.align as string | undefined
  const justifyClass = align === 'center' ? 'justify-center' : align === 'right' ? 'justify-end' : 'justify-start'

  const inner = variant === 'icon-only' ? (
    <span className="inline-flex items-center justify-center">
      {icon || <Zap className="h-5 w-5" />}
    </span>
  ) : (
    <span className="inline-flex items-center gap-2">
      {icon}
      {label}
    </span>
  )

  const btnProps = {
    onClick: href ? undefined : handleClick,
    children: inner,
  }

  const rendered = (() => {
    switch (variant) {
      case 'primary':
        return <PrimaryBtn accent={accent} {...btnProps} />
      case 'secondary':
        return <SecondaryBtn {...btnProps} />
      case 'outline':
        return <OutlineBtn accent={accent} {...btnProps} />
      case 'ghost':
        return <GhostBtn accent={accent} {...btnProps} />
      case 'glow':
        return <GlowBtn accent={accent} {...btnProps} />
      case '3d':
        return <ThreeDBtn accent={accent} {...btnProps} />
      case 'brutalist':
        return <BrutalistBtn accent={accent} {...btnProps} />
      case 'expand':
        return <ExpandBtn accent={accent} {...btnProps} />
      case 'splash':
        return <SplashBtn accent={accent} {...btnProps} />
      case 'neon':
        return <NeonBtn accent={accent} {...btnProps} />
      case 'gradient':
        return <GradientBtn accent={accent} {...btnProps} />
      case 'pill':
        return <PillBtn accent={accent} {...btnProps} />
      case 'underline':
        return <UnderlineBtn accent={accent} {...btnProps} />
      case 'icon-only':
        return <IconOnlyBtn accent={accent} {...btnProps} />
      default:
        return <PrimaryBtn accent={accent} {...btnProps} />
    }
  })()

  if (href) {
    return (
      <div className={`flex ${justifyClass}`}>
        <a href={href} target={newTab ? '_blank' : undefined} rel={newTab ? 'noopener noreferrer' : undefined}>
          {rendered}
        </a>
      </div>
    )
  }

  return <div className={`flex ${justifyClass}`}>{rendered}</div>
}

/* ========== Base variants ========== */

interface BtnProps {
  accent?: string
  onClick?: () => void
  children: React.ReactNode
}

function PrimaryBtn({ accent, onClick, children }: BtnProps) {
  return (
    <button onClick={onClick} className="px-5 py-2.5 rounded-lg font-medium text-sm text-white transition-all hover:opacity-90 cursor-pointer" style={{ backgroundColor: accent }}>
      {children}
    </button>
  )
}

function SecondaryBtn({ onClick, children }: BtnProps) {
  return (
    <button onClick={onClick} className="px-5 py-2.5 rounded-lg font-medium text-sm bg-slate-100 text-slate-900 hover:bg-slate-200 border border-slate-200 transition-all cursor-pointer">
      {children}
    </button>
  )
}

function OutlineBtn({ accent, onClick, children }: BtnProps) {
  return (
    <button onClick={onClick} className="px-5 py-2.5 rounded-lg font-medium text-sm bg-transparent border-2 transition-all hover:opacity-80 cursor-pointer" style={{ borderColor: accent, color: accent }}>
      {children}
    </button>
  )
}

function GhostBtn({ accent, onClick, children }: BtnProps) {
  return (
    <button onClick={onClick} className="px-5 py-2.5 rounded-lg font-medium text-sm bg-transparent transition-all hover:opacity-70 cursor-pointer" style={{ color: accent }}>
      {children}
    </button>
  )
}

/* ========== Fun variants ========== */

function GlowBtn({ accent, onClick, children }: BtnProps) {
  return (
    <button
      onClick={onClick}
      className="relative px-6 py-3 rounded-xl font-bold text-sm text-white transition-all hover:scale-105 cursor-pointer"
      style={{
        backgroundColor: accent,
        boxShadow: `0 0 20px ${accent}, 0 0 40px color-mix(in srgb, ${accent} 40%, transparent), 0 0 60px color-mix(in srgb, ${accent} 20%, transparent)`,
      }}
    >
      {children}
    </button>
  )
}

function ThreeDBtn({ accent, onClick, children }: BtnProps) {
  return (
    <button
      onClick={onClick}
      className="relative px-6 py-3 rounded-xl font-bold text-sm text-white transition-all active:translate-y-1 active:shadow-none cursor-pointer"
      style={{
        backgroundColor: accent,
        boxShadow: `0 6px 0 color-mix(in srgb, ${accent} 70%, black), 0 8px 12px rgba(0,0,0,0.15)`,
        transform: 'translateY(-2px)',
      }}
    >
      {children}
    </button>
  )
}

function BrutalistBtn({ accent, onClick, children }: BtnProps) {
  return (
    <button
      onClick={onClick}
      className="relative px-6 py-3 font-black text-sm uppercase tracking-wider border-3 transition-all hover:translate-x-[-2px] hover:translate-y-[-2px] cursor-pointer"
      style={{
        color: accent,
        borderWidth: '3px',
        borderColor: accent,
        boxShadow: `4px 4px 0 ${accent}`,
        backgroundColor: 'white',
      }}
    >
      {children}
    </button>
  )
}

function ExpandBtn({ accent, onClick, children }: BtnProps) {
  return (
    <button
      onClick={onClick}
      className="relative px-6 py-3 rounded-full font-bold text-sm overflow-hidden transition-all hover:scale-105 cursor-pointer group"
      style={{ color: accent, border: `2px solid ${accent}` }}
    >
      <span
        className="absolute inset-0 scale-0 group-hover:scale-100 transition-transform duration-300 rounded-full origin-center"
        style={{ backgroundColor: accent }}
      />
      <span className="relative z-10 transition-colors duration-300 group-hover:text-white flex items-center gap-2 [&>svg]:group-hover:scale-125 [&>svg]:transition-transform [&>svg]:duration-300">
        {children}
      </span>
    </button>
  )
}

function SplashBtn({ accent, onClick, children }: BtnProps) {
  return (
    <button onClick={onClick} className="relative px-8 py-4 font-bold text-sm text-white transition-all hover:scale-105 cursor-pointer group">
      <svg className="absolute -inset-3 w-[calc(100%+24px)] h-[calc(100%+24px)]" viewBox="0 0 240 100" preserveAspectRatio="none">
        {/* Main splash */}
        <path
          d="M 30,15 C 60,2 180,2 210,15 C 225,22 230,35 225,50 C 230,65 225,78 210,85 C 180,98 60,98 30,85 C 15,78 10,65 15,50 C 10,35 15,22 30,15 Z"
          fill={accent}
        />
        {/* Splatter drops */}
        <circle cx="15" cy="25" r="6" fill={accent} />
        <circle cx="228" cy="30" r="5" fill={accent} />
        <circle cx="225" cy="72" r="7" fill={accent} />
        <circle cx="12" cy="70" r="4" fill={accent} />
        <circle cx="45" cy="5" r="4" fill={accent} />
        <circle cx="195" cy="8" r="3" fill={accent} />
        <circle cx="200" cy="92" r="4" fill={accent} />
        <circle cx="35" cy="90" r="5" fill={accent} />
        {/* Tiny drops */}
        <circle cx="5" cy="45" r="2.5" fill={accent} />
        <circle cx="235" cy="50" r="2" fill={accent} />
        <circle cx="70" cy="0" r="2" fill={accent} />
        <circle cx="170" cy="98" r="2.5" fill={accent} />
      </svg>
      <span className="relative z-10">{children}</span>
    </button>
  )
}

function NeonBtn({ accent, onClick, children }: BtnProps) {
  return (
    <button
      onClick={onClick}
      className="relative px-6 py-3 rounded-lg font-bold text-sm bg-transparent border-2 transition-all hover:scale-105 cursor-pointer"
      style={{
        borderColor: accent,
        color: accent,
        textShadow: `0 0 10px ${accent}, 0 0 20px color-mix(in srgb, ${accent} 50%, transparent)`,
        boxShadow: `inset 0 0 20px color-mix(in srgb, ${accent} 10%, transparent), 0 0 15px color-mix(in srgb, ${accent} 30%, transparent)`,
      }}
    >
      {children}
    </button>
  )
}

function GradientBtn({ accent, onClick, children }: BtnProps) {
  return (
    <button
      onClick={onClick}
      className="px-6 py-3 rounded-xl font-bold text-sm text-white transition-all hover:scale-105 hover:shadow-lg cursor-pointer"
      style={{
        background: `linear-gradient(135deg, ${accent}, color-mix(in srgb, ${accent} 60%, #ec4899))`,
      }}
    >
      {children}
    </button>
  )
}

function PillBtn({ accent, onClick, children }: BtnProps) {
  return (
    <button
      onClick={onClick}
      className="px-8 py-2.5 rounded-full font-semibold text-sm text-white transition-all hover:shadow-lg hover:scale-105 cursor-pointer"
      style={{ backgroundColor: accent }}
    >
      {children}
    </button>
  )
}

function UnderlineBtn({ accent, onClick, children }: BtnProps) {
  return (
    <button
      onClick={onClick}
      className="relative px-2 py-1 font-bold text-sm bg-transparent transition-all hover:opacity-80 cursor-pointer group"
      style={{ color: accent }}
    >
      {children}
      <span
        className="absolute bottom-0 left-0 w-full h-0.5 transition-transform origin-left group-hover:scale-x-100 scale-x-75"
        style={{ backgroundColor: accent }}
      />
    </button>
  )
}

function IconOnlyBtn({ accent, onClick, children }: BtnProps) {
  return (
    <button
      onClick={onClick}
      className="w-12 h-12 rounded-full font-bold text-sm text-white transition-all hover:scale-110 hover:shadow-lg cursor-pointer flex items-center justify-center [&>span]:gap-0 [&>span>svg]:h-5 [&>span>svg]:w-5"
      style={{ backgroundColor: accent }}
    >
      {children}
    </button>
  )
}
